/**
 * Google Maps API Quota Monitoring System
 * Tracks usage and prevents overage charges
 */

const QUOTA_STORAGE_KEY = 'googleMapsQuota';

// New Google Maps Platform free tier limits (March 2025)
// Using Essentials tier (most generous) - 10,000 free calls per SKU per month
const MONTHLY_FREE_LIMITS = {
  maps: 10000,      // Maps JavaScript API - Essentials tier
  places: 10000,    // Places API (Autocomplete) - Essentials tier
  geocoding: 10000  // Geocoding API - Essentials tier
};

// Set our safety limits to 95% of free tier to avoid charges
const MONTHLY_LIMITS = {
  maps: Math.floor(MONTHLY_FREE_LIMITS.maps * 0.95),      // 9,500 calls/month
  places: Math.floor(MONTHLY_FREE_LIMITS.places * 0.95),  // 9,500 calls/month
  geocoding: Math.floor(MONTHLY_FREE_LIMITS.geocoding * 0.95) // 9,500 calls/month
};

// Calculate daily limits based on monthly (spread evenly across 30 days)
const DAILY_LIMITS = {
  maps: Math.floor(MONTHLY_LIMITS.maps / 30),        // ~317/day
  places: Math.floor(MONTHLY_LIMITS.places / 30),    // ~317/day
  geocoding: Math.floor(MONTHLY_LIMITS.geocoding / 30) // ~317/day
};

// Get current date in YYYY-MM-DD format (local time, not UTC)
// Using local time ensures the daily reset aligns with local midnight
// rather than UTC midnight (which falls at 10 AM AEST for UTC+10).
const getCurrentDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get current month in YYYY-MM format (local time, not UTC)
const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Get quota data for today and this month
const getQuotaData = () => {
  const today = getCurrentDate();
  const currentMonth = getCurrentMonth();
  const stored = localStorage.getItem(QUOTA_STORAGE_KEY);
  
  if (!stored) {
    return {
      date: today,
      month: currentMonth,
      daily: { maps: 0, places: 0, geocoding: 0 },
      monthly: { maps: 0, places: 0, geocoding: 0 },
      disabled: false
    };
  }
  
  const quota = JSON.parse(stored);
  
  // Handle migration from old format to new format
  if (!quota.daily || !quota.monthly) {
    // Old format - migrate to new structure
    const oldMaps = quota.maps || 0;
    const oldPlaces = quota.places || 0;
    const oldGeocoding = quota.geocoding || 0;
    
    quota.daily = { maps: oldMaps, places: oldPlaces, geocoding: oldGeocoding };
    quota.monthly = { maps: oldMaps, places: oldPlaces, geocoding: oldGeocoding };
    quota.month = currentMonth;
    
    // Remove old properties
    delete quota.maps;
    delete quota.places;
    delete quota.geocoding;
  }
  
  let needsSave = false;

  // Reset daily if it's a new day
  if (quota.date !== today) {
    quota.date = today;
    quota.daily = { maps: 0, places: 0, geocoding: 0 };
    needsSave = true;
  }
  
  // Reset monthly if it's a new month
  if (quota.month !== currentMonth) {
    quota.month = currentMonth;
    quota.monthly = { maps: 0, places: 0, geocoding: 0 };
    quota.disabled = false; // Re-enable for new month
    needsSave = true;
  }

  // Persist resets immediately so the next read sees the correct state
  if (needsSave) {
    localStorage.setItem(QUOTA_STORAGE_KEY, JSON.stringify(quota));
  }
  
  return quota;
};

// Legacy function for backward compatibility
const getTodayQuota = () => {
  const quota = getQuotaData();
  
  // Safety checks to ensure properties exist
  const daily = quota.daily || { maps: 0, places: 0, geocoding: 0 };
  
  return {
    date: quota.date,
    maps: daily.maps || 0,
    places: daily.places || 0,
    geocoding: daily.geocoding || 0,
    disabled: quota.disabled || false
  };
};

// Update quota usage
const updateQuota = (apiType, count = 1) => {
  const quota = getQuotaData();
  
  // Update both daily and monthly counters
  quota.daily[apiType] = (quota.daily[apiType] || 0) + count;
  quota.monthly[apiType] = (quota.monthly[apiType] || 0) + count;
  
  // Just track usage - no automatic disabling
  
  localStorage.setItem(QUOTA_STORAGE_KEY, JSON.stringify(quota));

  // Notify same-tab listeners (storage event only fires in other tabs)
  window.dispatchEvent(new CustomEvent('googleMapsQuotaUpdated'));
  
  // Return legacy format for backward compatibility
  return {
    date: quota.date,
    maps: quota.daily.maps,
    places: quota.daily.places,
    geocoding: quota.daily.geocoding,
    disabled: quota.disabled
  };
};

// Check if maps are available (not disabled by quota)
const isMapsAvailable = () => {
  const quota = getTodayQuota();
  return !quota.disabled;
};

// Get current usage statistics
const getUsageStats = () => {
  const quota = getQuotaData();
  
  // Safety checks to ensure properties exist
  const daily = quota.daily || { maps: 0, places: 0, geocoding: 0 };
  const monthly = quota.monthly || { maps: 0, places: 0, geocoding: 0 };
  
  return {
    maps: {
      used: daily.maps || 0,
      limit: DAILY_LIMITS.maps,
      percentage: Math.round(((daily.maps || 0) / DAILY_LIMITS.maps) * 100),
      monthlyUsed: monthly.maps || 0,
      monthlyLimit: MONTHLY_LIMITS.maps,
      monthlyPercentage: Math.round(((monthly.maps || 0) / MONTHLY_LIMITS.maps) * 100)
    },
    places: {
      used: daily.places || 0,
      limit: DAILY_LIMITS.places,
      percentage: Math.round(((daily.places || 0) / DAILY_LIMITS.places) * 100),
      monthlyUsed: monthly.places || 0,
      monthlyLimit: MONTHLY_LIMITS.places,
      monthlyPercentage: Math.round(((monthly.places || 0) / MONTHLY_LIMITS.places) * 100)
    },
    geocoding: {
      used: daily.geocoding || 0,
      limit: DAILY_LIMITS.geocoding,
      percentage: Math.round(((daily.geocoding || 0) / DAILY_LIMITS.geocoding) * 100),
      monthlyUsed: monthly.geocoding || 0,
      monthlyLimit: MONTHLY_LIMITS.geocoding,
      monthlyPercentage: Math.round(((monthly.geocoding || 0) / MONTHLY_LIMITS.geocoding) * 100)
    },
    disabled: quota.disabled || false,
    resetsAt: quota.disabled && Object.values(monthly).some(v => v >= MONTHLY_LIMITS[Object.keys(MONTHLY_LIMITS)[0]]) 
      ? 'Next month at 12:00 AM' 
      : 'Tomorrow at 12:00 AM'
  };
};

// Manually disable maps (emergency stop)
const disableMaps = () => {
  const quota = getQuotaData();
  quota.disabled = true;
  localStorage.setItem(QUOTA_STORAGE_KEY, JSON.stringify(quota));
};

// Enable maps (manual override)
const enableMaps = () => {
  const quota = getQuotaData();
  quota.disabled = false;
  localStorage.setItem(QUOTA_STORAGE_KEY, JSON.stringify(quota));
};

// Reset monthly counters (for new billing cycle)
const resetMonthlyQuota = () => {
  const quota = getQuotaData();
  quota.monthly = { maps: 0, places: 0, geocoding: 0 };
  quota.disabled = false; // Re-enable when resetting
  quota.month = getCurrentMonth();
  localStorage.setItem(QUOTA_STORAGE_KEY, JSON.stringify(quota));
};

// Get total usage for all time (for analytics)
const getTotalUsage = () => {
  const quota = getQuotaData();
  return {
    daily: quota.daily || { maps: 0, places: 0, geocoding: 0 },
    monthly: quota.monthly || { maps: 0, places: 0, geocoding: 0 },
    status: quota.disabled ? 'disabled' : 'enabled',
    lastUpdate: quota.date,
    currentMonth: quota.month
  };
};

// Clear all quota data (for troubleshooting)
const clearQuotaData = () => {
  localStorage.removeItem(QUOTA_STORAGE_KEY);
};

// Check if approaching limit (80% of monthly free tier threshold)
const isApproachingLimit = () => {
  const stats = getUsageStats();
  return Object.values(stats)
    .filter(stat => stat.monthlyPercentage !== undefined)
    .some(stat => stat.monthlyPercentage >= 80);
};

export {
  isMapsAvailable,
  updateQuota,
  getUsageStats,
  disableMaps,
  enableMaps,
  resetMonthlyQuota,
  getTotalUsage,
  clearQuotaData,
  isApproachingLimit,
  DAILY_LIMITS,
  MONTHLY_LIMITS,
  MONTHLY_FREE_LIMITS
};
